import type { TPayerChainContext, TSelectedPayer } from './types';
import { EPayerStep } from './types';

// payer 名称匹配：小写 + 去空格后 includes——后端名字有 "United Healthcare"（带空格）/"UnitedHealthcare" 两种写法
const nameHas = (p: TSelectedPayer, kw: string) => p.payerName.toLowerCase().replace(/\s+/g, '').includes(kw);

const isAetna = (p: TSelectedPayer) => nameHas(p, 'aetna');
const isMedicare = (p: TSelectedPayer) => nameHas(p, 'medicare');
const isMedicaid = (p: TSelectedPayer) => nameHas(p, 'medicaid');
// Humana 同时有 Medicare Advantage 与 Medicaid 线，选了等同于 Medicare + Medicaid（09/16 ops）
const isHumana = (p: TSelectedPayer) => nameHas(p, 'humana');
const isMolina = (p: TSelectedPayer) => nameHas(p, 'molina');
// Blue plan 不按名字认（Florida Blue / Independence / Premera / Medical Mutual of Ohio 名字里都没有 bcbs），
// 看 payer 列表接口的 payerType
const isBluePlan = (p: TSelectedPayer) => p.payerType === 'Blue';
// Optum 是 UHC 旗下网络，不可单独选择，所以触发条件只看 UnitedHealthcare（原型同款处理）
// Blue Shield of California 只签有 104 designation 的加州 NP（10/26）；只算加州那条 panel
const isBlueShieldCa = (p: TSelectedPayer) => p.state === 'CA' && nameHas(p, 'blueshieldofcalifornia');
const isUhc = (p: TSelectedPayer) => nameHas(p, 'unitedhealthcare') || nameHas(p, 'uhc') || nameHas(p, 'optum');

export const selectedStates = (selected: TSelectedPayer[]): string[] => {
    const out: string[] = [];
    selected.forEach((p) => {
        if (p.state && !out.includes(p.state)) out.push(p.state);
    });
    return out;
};

// UHC W-9 触发（09/01 重构）：选了 UHC 就触发，不看州数——W-9 按「Tax ID + 表上名字」计份，
// 与州无关（单州也可能有多个 TIN，多州也可能只有一个），州数量说明不了 W-9 数量
export const optumTriggered = (selected: TSelectedPayer[]): boolean => selected.some(isUhc);

// Gov 步骤触发：选了 Aetna / Medicare / Medicaid / Humana 任一；两个问题（Medicare/Medicaid enrolled?）按州各答一次
export const govTriggered = (selected: TSelectedPayer[]): boolean =>
    selected.some((p) => isAetna(p) || isMedicare(p) || isMedicaid(p) || isHumana(p));

// IRS letter 触发（原型 IRS_LETTER_PAYERS，10/19）：Medicare / Medicaid / Molina / 任一 Blue plan。Humana 不算
export const irsLetterTriggered = (selected: TSelectedPayer[]): boolean =>
    selected.some((p) => isMedicare(p) || isMedicaid(p) || isMolina(p) || isBluePlan(p));

// Medicare payout 触发：只看 Medicare 本身（原型 hasMedicareSelection，Humana 不算）
export const medicareTriggered = (selected: TSelectedPayer[]): boolean => selected.some(isMedicare);

/**
 * 计算选完 payer 后需要走的条件步骤链（顺序固定）。
 * 触发条件满足就显示——已保存过的数据由宿主水合反填，不跳过步骤：
 * - Gov program：选了 Aetna/Medicare/Medicaid/Humana 任一
 * - Service address：选了任何 payer
 * - Optum W-9：选了 UHC 且总州数 > 1（所有选了 payer 的州各传一份，不限 UHC 州）
 * - Aetna Focus：选了 Aetna
 * - IRS letter / Medicare payout：需要 ctx（见 TPayerChainContext），不传 ctx 时不出现
 */
export const getApplicablePayerSteps = (selected: TSelectedPayer[], ctx?: TPayerChainContext): EPayerStep[] => {
    const steps: EPayerStep[] = [];
    const states = selectedStates(selected);
    if (!states.length) return steps;

    // 104（10/26）：链的第一步，只问加州执照的 NP，且选了 Blue Shield of California
    if (ctx?.needs104 && ctx.np104 !== true && selected.some(isBlueShieldCa)) {
        steps.push(EPayerStep.NP_104);
    }
    if (govTriggered(selected)) {
        steps.push(EPayerStep.GOV_PROGRAM);
    }
    if (ctx && ctx.billsUnderTaxId && !ctx.irsLetterOnFile && irsLetterTriggered(selected)) {
        steps.push(EPayerStep.IRS_LETTER);
    }
    steps.push(EPayerStep.SERVICE_ADDRESS);
    if (ctx && medicareTriggered(selected) && (ctx.group || !ctx.payoutOnFile)) {
        steps.push(EPayerStep.MEDICARE_PAYOUT);
    }
    if (optumTriggered(selected)) {
        steps.push(EPayerStep.OPTUM_W9);
    }
    if (selected.some(isAetna)) {
        steps.push(EPayerStep.AETNA_FOCUS);
    }
    return steps;
};

// 各步的州维度口径：Gov/Service address 按全部选中州；UHC W-9、Aetna Focus、IRS letter、payout 无州维度
// （只看是否新触发——ADD 场景已有的不再弹）
const STATELESS_STEPS = [
    EPayerStep.NP_104,
    EPayerStep.OPTUM_W9,
    EPayerStep.AETNA_FOCUS,
    EPayerStep.IRS_LETTER,
    EPayerStep.MEDICARE_PAYOUT,
];
const stepStates = (step: EPayerStep, selected: TSelectedPayer[]): string[] => {
    if (STATELESS_STEPS.includes(step)) return [];
    return selectedStates(selected);
};

/**
 * ADD 场景的链计算：只保留相对已有 panel「有变化」的步骤——
 * 基线（仅已有 panel）没触发的步骤，或步骤州列表里出现了基线没有的州；
 * 其余步骤视为之前已确认过，不再弹出（已存数据保持不动）。
 */
export const getChangedPayerSteps = (
    existing: TSelectedPayer[],
    union: TSelectedPayer[],
    ctx?: TPayerChainContext,
): EPayerStep[] => {
    const baseSteps = getApplicablePayerSteps(existing, ctx);
    // 这几步出不出现已经按「存没存过」判断（IRS letter 看 irsLetterOnFile、104 看 np104、solo payout 看
    // payoutOnFile），不能再拿已有 panel 当「已确认」过滤：已有 panel 早于这一步上线时 provider 从没填过。
    // group 的 payout 是 practice 共享账户、每次都出，已有 panel 时仍视为已确认
    const selfGated = (step: EPayerStep) =>
        step === EPayerStep.IRS_LETTER ||
        step === EPayerStep.NP_104 ||
        (step === EPayerStep.MEDICARE_PAYOUT && !ctx?.group);
    return getApplicablePayerSteps(union, ctx).filter((step) => {
        if (selfGated(step)) return true;
        if (!baseSteps.includes(step)) return true;
        const baseStates = stepStates(step, existing);
        return stepStates(step, union).some((st) => !baseStates.includes(st));
    });
};

// 104 答 No 之后要从选择里去掉的那条（Blue Shield of California 的加州 panel）
export const isNp104BlockedPayer = isBlueShieldCa;

// 首次提交（solo INITIAL、group 替 provider 选 payer）：每一步都出，之前填过的回填给 provider 再确认一遍
// （10/27 定，覆盖原型 solo payout / IRS letter 填过即跳过）。只在算链时把「已有」当成没有，回填数据另取；
// 提交后加 payer 仍按「已有就跳过」
export const firstSubmitContext = (ctx: TPayerChainContext): TPayerChainContext => ({
    ...ctx,
    irsLetterOnFile: false,
    payoutOnFile: false,
    np104: null,
});
